import { motion } from 'framer-motion'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle, Clock, FileText, Monitor, User } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

export default function TestInstructionsPage() {
    const navigate = useNavigate()
    const { testId } = useParams()
    const [candidateName, setCandidateName] = useState('')
    const [rollNumber, setRollNumber] = useState('')
    const [agreedToTerms, setAgreedToTerms] = useState(false)

    const handleStartTest = () => {
        if (!candidateName || !rollNumber || !agreedToTerms) {
            alert('Please fill all details and agree to the terms')
            return
        }
        // Navigate to student portal test interface
        navigate(`/student/test/exam/${testId}`, {
            state: { candidateName, rollNumber }
        })
    }

    return (
        <div className="min-h-screen">
            {/* Student Portal Header */}
            <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-bold text-theme-primary">Test Instructions</h1>
                            <p className="text-sm text-theme-secondary">Student Portal</p>
                        </div>
                        <button
                            onClick={() => navigate('/student/dashboard')}
                            className="px-4 py-2 text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-theme-primary rounded-lg transition-colors"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </header>

            <div className="container mx-auto px-4 py-12">
                <div className="max-w-5xl mx-auto">
                    {/* Welcome Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-8"
                    >
                        <h1 className="text-3xl md:text-4xl font-display font-bold text-theme-primary mb-4">
                            Welcome to <span className="gradient-text">NEET Mock Test</span>
                        </h1>
                        <p className="text-lg text-theme-secondary">
                            Please read all instructions carefully before starting the test
                        </p>
                    </motion.div>

                    {/* Candidate Details Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <GlassmorphicCard variant="elevated" className="p-8 mb-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-xl dark:shadow-none">
                            <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6 flex items-center gap-2">
                                <User className="h-6 w-6 text-primary-blue" />
                                Candidate Details
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-2">
                                        Candidate Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={candidateName}
                                        onChange={(e) => setCandidateName(e.target.value)}
                                        placeholder="Enter your full name"
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-2">
                                        Roll Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={rollNumber}
                                        onChange={(e) => setRollNumber(e.target.value)}
                                        placeholder="Enter your roll number"
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                    />
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Test Information */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <GlassmorphicCard className="p-8 mb-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-lg dark:shadow-none">
                            <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6 flex items-center gap-2">
                                <FileText className="h-6 w-6 text-primary-blue" />
                                Test Information
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="text-center p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-100 dark:border-transparent">
                                    <Clock className="h-8 w-8 text-primary-blue mx-auto mb-2" />
                                    <p className="text-sm text-theme-muted mb-1">Duration</p>
                                    <p className="text-xl font-bold text-theme-primary">3 hrs 20 min</p>
                                </div>
                                <div className="text-center p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-100 dark:border-transparent">
                                    <FileText className="h-8 w-8 text-primary-blue mx-auto mb-2" />
                                    <p className="text-sm text-theme-muted mb-1">Total Questions</p>
                                    <p className="text-xl font-bold text-theme-primary">200</p>
                                </div>
                                <div className="text-center p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-100 dark:border-transparent">
                                    <Monitor className="h-8 w-8 text-primary-blue mx-auto mb-2" />
                                    <p className="text-sm text-theme-muted mb-1">Test Pattern</p>
                                    <p className="text-xl font-bold text-theme-primary">NTA Official</p>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* General Instructions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <GlassmorphicCard className="p-8 mb-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-lg dark:shadow-none">
                            <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6">
                                General Instructions
                            </h2>
                            <div className="space-y-4 text-theme-secondary">
                                <div className="flex items-start gap-3">
                                    <span className="text-primary-blue font-bold mt-1">1.</span>
                                    <p>The test comprises of <strong className="text-theme-primary">200 questions</strong> divided into three sections: Physics (50), Chemistry (50), and Biology (100).</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-primary-blue font-bold mt-1">2.</span>
                                    <p>Each section has two parts: Section A (35 questions) and Section B (15 questions, attempt any 10).</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-primary-blue font-bold mt-1">3.</span>
                                    <p>Total duration of the examination is <strong className="text-theme-primary">200 minutes (3 hours 20 minutes)</strong>.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-primary-blue font-bold mt-1">4.</span>
                                    <p>The clock will be set at the server. The countdown timer in the top right corner will display the remaining time to complete the exam.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="text-primary-blue font-bold mt-1">5.</span>
                                    <p>The test will automatically submit when the timer reaches zero. You cannot continue after time expires.</p>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Marking Scheme */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <GlassmorphicCard className="p-8 mb-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-lg dark:shadow-none">
                            <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6">
                                Marking Scheme
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                                    <span className="text-theme-secondary">Correct Answer</span>
                                    <span className="text-green-600 dark:text-green-400 font-bold text-lg">+4 Marks</span>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                    <span className="text-theme-secondary">Incorrect Answer</span>
                                    <span className="text-red-600 dark:text-red-400 font-bold text-lg">-1 Mark</span>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-700/20 border border-slate-200 dark:border-slate-600/30 rounded-lg">
                                    <span className="text-theme-secondary">Not Attempted</span>
                                    <span className="text-slate-500 dark:text-slate-400 font-bold text-lg">0 Marks</span>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Navigating Instructions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <GlassmorphicCard className="p-8 mb-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-lg dark:shadow-none">
                            <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6">
                                Navigating to a Question
                            </h2>
                            <div className="space-y-4 text-theme-secondary">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-primary-blue mt-1 flex-shrink-0" />
                                    <p>To answer a question, click on the question number in the Question Palette to go to that question directly.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-primary-blue mt-1 flex-shrink-0" />
                                    <p>Click on <strong className="text-theme-primary">Save & Next</strong> to save your answer and move to the next question.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-primary-blue mt-1 flex-shrink-0" />
                                    <p>Click on <strong className="text-theme-primary">Mark for Review & Next</strong> to mark the question for review and move to next.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-primary-blue mt-1 flex-shrink-0" />
                                    <p>Click on <strong className="text-theme-primary">Clear Response</strong> to clear your selected answer.</p>
                                </div>
                            </div>

                            <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-100 dark:border-transparent">
                                <p className="text-sm font-semibold text-theme-primary mb-3">Question Status Legend:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-green-500 flex items-center justify-center text-white text-sm font-bold">1</div>
                                        <span className="text-xs text-theme-secondary">Answered</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-red-500 flex items-center justify-center text-white text-sm font-bold">2</div>
                                        <span className="text-xs text-theme-secondary">Not Answered</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-slate-400 dark:bg-slate-600 flex items-center justify-center text-white text-sm font-bold">3</div>
                                        <span className="text-xs text-theme-secondary">Not Visited</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-purple-500 flex items-center justify-center text-white text-sm font-bold">4</div>
                                        <span className="text-xs text-theme-secondary">Marked for Review</span>
                                    </div>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Important Notice */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                    >
                        <GlassmorphicCard variant="elevated" className="p-8 mb-8 border-2 border-accent-gold/30 bg-white/95 dark:bg-slate-900/60 shadow-xl dark:shadow-none">
                            <div className="flex items-start gap-4">
                                <AlertTriangle className="h-8 w-8 text-accent-gold flex-shrink-0" />
                                <div>
                                    <h3 className="text-xl font-display font-semibold text-theme-primary mb-4">
                                        Important Notice
                                    </h3>
                                    <ul className="space-y-2 text-theme-secondary">
                                        <li className="flex items-start gap-2">
                                            <span className="text-accent-gold mt-1">•</span>
                                            <span>Please ensure you have a stable internet connection</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-accent-gold mt-1">•</span>
                                            <span>Do not press the back button or refresh the page during the test</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-accent-gold mt-1">•</span>
                                            <span>Do not close the browser window until you submit the test</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-accent-gold mt-1">•</span>
                                            <span>Make sure to submit your test before the timer expires</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Declaration */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                    >
                        <GlassmorphicCard className="p-8 mb-8 bg-white/95 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 shadow-lg dark:shadow-none">
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    className="mt-1 w-5 h-5 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-primary-blue focus:ring-primary-blue focus:ring-offset-0"
                                />
                                <label htmlFor="terms" className="text-theme-secondary cursor-pointer">
                                    I have read and understood all the instructions. I agree to follow all the guidelines and rules mentioned above.
                                    I understand that any violation may lead to cancellation of my test.
                                </label>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="flex gap-4 justify-center"
                    >
                        <button
                            onClick={() => navigate('/student/dashboard')}
                            className="px-8 py-4 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-theme-primary font-semibold rounded-lg transition-colors"
                        >
                            Back to Dashboard
                        </button>
                        <button
                            onClick={handleStartTest}
                            disabled={!candidateName || !rollNumber || !agreedToTerms}
                            className="px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed glow-primary"
                        >
                            I am ready to begin
                        </button>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}
